<template>
  <div>
    <div class="date-sub-header">
      <SubHeaderItem
        v-if="parametrage && parametrage.debutInscription"
        class="date-inscription"
        :icon="'calendar'"
      >
        <div class="item-text">{{ $t("myProjects.dateInscription") }}</div>
        <div class="item-text range">
          {{
            $t("myProjects.dateInscriptionRange", [
              formatDate(parametrage.debutInscription),
              formatDate(parametrage.finInscription),
            ])
          }}
        </div>
      </SubHeaderItem>
      <SubHeaderItem
        v-if="parametrage && parametrage.debutLabellisation && showLabellisationDates"
        class="date-labellisation"
        :icon="'calendar'"
      >
        <div class="item-text">{{ $t("projetDetails.dateLabellisation") }}</div>
        <div class="item-text range">
          {{
            $t("myProjects.dateInscriptionRange", [
              formatDate(parametrage.debutLabellisation),
              formatDate(parametrage.finLabellisation),
            ])
          }}
        </div>
      </SubHeaderItem>
    </div>
    <PageView :main-title="$t('myProjects.title')" :no-sub-title-expected="true">
      <ContentView class="MyProjectsPage-content">
        <div class="MyProjectsPage-projects-part">
          <div v-if="pendingProjects && pendingProjects.length > 0">
            <H3TitleView>{{ $t("myProjects.pendingInvit") }}</H3TitleView>
            <div class="MyProjectsPage-cards">
              <div v-if="isLoadingData">
                <BookmarkCard
                  :is-loading="true"
                  :tag-type="tagTypes.INVITATION"
                  icon-name="paper-plane"
                />
              </div>
              <div v-for="project in pendingProjects" v-else :key="project.id">
                <PendingProjectCard
                  :project="project"
                  :logged-user-id="loggedUser ? loggedUser.id : ''"
                  @invitation-processed="invitationProcessed()"
                  @project-updated="updateProject"
                />
              </div>
            </div>
          </div>
          <H3TitleView>{{ $t("myProjects.ae-projects") }}</H3TitleView>
          <div class="MyProjectsPage-buttons">
            <FilterComponent
              :filter-fields="filterFields"
              :initial-filters="activeFilters"
              @update-active-filters="updateFilters"
            />

            <b-button
              rounded
              type="is-primary"
              :disabled="!showCreateLink"
              :label="$t('myProjects.createProject')"
              @click="createProject"
            />
          </div>

          <b-message
            v-if="isPorteurProjet && showCreateLink"
            type="is-info"
            style="margin-top: 20px; margin-bottom: -20px"
          >
            <p>{{ $t("myProjects.info-porteur-projet") }}</p>
          </b-message>

          <LoadingSkeleton v-if="isLoadingData" :loading-text="$t('myProjects.loading')" />

          <div v-else class="MyProjectsPage-cards">
            <div v-for="projectLight in myProjectLights" :key="projectLight.projetId">
              <ProjectCard
                :project="projectLight"
                :logged-user="loggedUser"
                @delete-project="loadProjects()"
              />
            </div>
            <b-message
              v-if="myProjects.length === 0 && activeFilters.length === 0"
              type="is-warning"
            >
              {{ $t("myProjects.noProjects") }}
            </b-message>
            <b-message
              v-if="myProjects.length === 0 && activeFilters.length !== 0"
              type="is-warning"
            >
              {{ $t("myProjects.noProjectsFiltered") }}
            </b-message>
          </div>
        </div>
      </ContentView>
      <FirstConnectionView
        :is-loading="isLoadingData"
        :pending-projects="pendingProjects"
        :projects-in-etablissement="projectsNotParticipatingIn"
        :logged-user="loggedUser"
        :is-component-modal-active="openFirstConnectionModal"
        @modal-closed="closeFirstConnectionModal"
        @send-answers="sendAnswers"
        @projects-joined="updateProjectsInEtablissement"
        @pending-project-updated="updateProject"
      />
      <router-view
        :key="$route.fullPath"
        :logged-user="loggedUser"
        :chosen-etablissement="chosenEtablissement"
        :projects-in-etablissement="projectsInEtablissement"
        @etablissement-chosen="setChosenEtablissement"
        @cancel-project-creation="closeProjectCreation"
        @end-project-creation="endProjectCreation"
        @confirm-join="confirmJoinedProjects"
        @ask-project-creation="askForProjectCreation"
      />
    </PageView>
  </div>
</template>

<script lang="ts">
import { BMessage, BButton } from "buefy"

import { defineComponent } from "vue"
import type { EtatProjet } from "@/model/bean/GeneratedDTOs"
import {
  EtablissementDTO,
  FilterDTO,
  ParametrageDTO,
  ProjetAnnuelDTO,
  ProjetAnnuelLightDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import PageView from "@/views/commons/PageView.vue"
import ContentView from "@/views/commons/ContentView.vue"
import { LoginService } from "@/services/rest/login/LoginService"
import { ProjectService } from "@/services/rest/project/ProjectService"
import FirstConnectionView from "@/views/projects_pp/FirstConnectionView.vue"
import H3TitleView from "@/views/commons/H3TitleView.vue"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import PendingProjectCard from "@/views/projects_pp/PendingProjectCard.vue"
import { EnumHelper, TAG_TYPES_FOR_BOOKMARK_CARD } from "@/model/bean/EnumHelper"
import BookmarkCard from "@/views/generic_components/BookmarkCard.vue"
import FilterComponent, { FilterField } from "@/views/generic_components/FilterComponent.vue"
import ProjectCard from "@/views/projects_pp/ProjectCard.vue"
import { ParametersService } from "@/services/rest/parameters/ParametersService"
import TabParamsStore from "@/services/storage/TabParamsStore"
import SubHeaderItem from "@/views/commons/SubHeaderItem.vue"
import RouterUtils from "@/router/RouterUtils"
import LoadingSkeleton from "@/views/generic_components/LoadingSkeleton.vue"
import { UsersService } from "@/services/rest/users/UsersService"
import DateUtils from "@/services/DateUtils"

export default defineComponent({
  name: "MyProjectsPage",
  components: {
    PageView,
    ContentView,
    FirstConnectionView,
    H3TitleView,
    PendingProjectCard,
    BookmarkCard,
    FilterComponent,
    ProjectCard,
    SubHeaderItem,
    LoadingSkeleton,
    BButton,
    BMessage,
  },
  data() {
    return {
      // Services
      loginService: LoginService.INSTANCE,
      projectService: ProjectService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      usersService: UsersService.INSTANCE,
      parametersService: ParametersService.INSTANCE,

      // Variables métier
      openFirstConnectionModal: false,
      pendingProjects: [] as ProjetAnnuelDTO[],
      projectsInEtablissement: [] as ProjetAnnuelDTO[],
      myProjects: [] as ProjetAnnuelDTO[],
      myProjectLights: [] as ProjetAnnuelLightDTO[],
      isLoadingData: true,
      loggedUser: new UtilisateurDTO(),
      chosenEtablissement: new EtablissementDTO(),
      tagTypes: TAG_TYPES_FOR_BOOKMARK_CARD,
      filterFields: [] as FilterField[],
      projectsNotParticipatingIn: [] as ProjetAnnuelDTO[],
      parametrage: new ParametrageDTO(),
      showCreateLink: false,
      showLabellisationDates: false,
      isPorteurProjet: false,

      // Filters
      activeFilters: [] as Array<FilterDTO>,
    }
  },
  created(): void {
    const loggedUser = this.loginService.getLoggedUser()
    if (!loggedUser) {
      return
    }
    this.loggedUser = loggedUser
    this.activeFilters = TabParamsStore.INSTANCE.getLastFilters(
      (this as any).$router.currentRoute.value.path,
    )
    this.init()
  },
  methods: {
    async init(): Promise<void> {
      try {
        this.isLoadingData = true
        this.isPorteurProjet = this.usersService.isPorteurProjet(this.loggedUser)
        const isFirstConnection = !this.loggedUser.lastConnection

        this.createFilterFields()
        this.loadParameters(this.loggedUser)
        this.loadProjects()

        this.pendingProjects = await this.projectService.getPendingInvitationsToProject()

        if (isFirstConnection) {
          await this.setDataForFirstConnection()
        }
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t("myProjects.retrieve-project-error").toString(),
          e as Error,
        )
      }
      this.isLoadingData = false
    },

    async setDataForFirstConnection(): Promise<void> {
      if (this.loggedUser.etablissement) {
        this.projectsInEtablissement = await this.projectService.getEtablissementProjects(
          this.loggedUser.etablissement,
        )
        this.projectsNotParticipatingIn = this.projectsInEtablissement.filter(
          (p) => !p.enseignants.some((e) => e.id === this.loggedUser.id),
        )
      }

      this.openFirstConnectionModal =
        !this.loginService.haveUserBeenLoggedIn() &&
        (this.pendingProjects.length !== 0 || this.projectsNotParticipatingIn.length !== 0)
    },

    createFilterFields(): void {
      const newFFEtablissementName = new FilterField()
      newFFEtablissementName.field = "etablissement.nom"
      newFFEtablissementName.label = this.$t("myProjects.filter-labels.etablissement").toString()
      this.filterFields.push(newFFEtablissementName)

      const newFFname = new FilterField()
      newFFname.field = "projet.nom"
      newFFname.label = this.$t("myProjects.filter-labels.nom").toString()
      this.filterFields.push(newFFname)

      const newFFProjectStatus = new FilterField()
      newFFProjectStatus.field = "projet.etat"
      newFFProjectStatus.label = this.$t("myProjects.filter-labels.etats").toString()
      newFFProjectStatus.possibleSearchValues = this.getAvailableEtatsProjetList()
      this.filterFields.push(newFFProjectStatus)
    },

    getAvailableEtatsProjetList(): EtatProjet[] {
      let etats: EtatProjet[] = EnumHelper.allEtatsProjets
      if (
        this.usersService.hasAnyProfile(this.loggedUser, [
          PROFIL_UTILISATEUR.REFERENT,
          PROFIL_UTILISATEUR.ENSEIGNANT,
        ])
      ) {
        etats = etats.filter((e) => e !== "ARCHIVE")
      }
      return etats
    },

    updateFilters(filters: FilterDTO[]): void {
      this.activeFilters = filters
      TabParamsStore.INSTANCE.storeFilters((this as any).$router.currentRoute.value.path, filters)
      this.loadProjects()
    },

    closeFirstConnectionModal(): void {
      this.openFirstConnectionModal = false
      this.loginService.setUserHaveBeenLoggedIn()
      this.loadProjects()
    },

    async loadProjects(): Promise<void> {
      try {
        this.isLoadingData = true

        this.myProjects = await this.projectService.getProjetsForPorteurProjet(
          this.loggedUser,
          this.activeFilters,
        )
        this.myProjectLights = this.myProjects.map((p) =>
          this.projectService.projetAnnuelToLight(p),
        )

        this.showLabellisationDates =
          this.myProjects.filter((p) => p.typeDossier != "DOSSIER_INSCRIPTION").length > 0
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t("myProjects.retrieve-project-error").toString(),
          e as Error,
        )
      }
      this.isLoadingData = false
    },

    async loadParameters(loggedUser?: UtilisateurDTO): Promise<void> {
      await this.parametersService.getParametrage(loggedUser?.grae?.id).then((params) => {
        this.parametrage = params
        const today = new Date()
        this.showCreateLink = params.debutInscription < today && today < params.finInscription
      })
    },

    async invitationProcessed(): Promise<void> {
      this.loadProjects()
      this.pendingProjects = await this.projectService.getPendingInvitationsToProject()
    },

    updateProject(updatedProject: ProjetAnnuelDTO): void {
      // Mettre à jour le projet dans la liste des projets en attente
      const index = this.pendingProjects.findIndex((p) => p.id === updatedProject.id)
      if (index !== -1) {
        this.pendingProjects.splice(index, 1, updatedProject)
      }
    },

    async sendAnswers(closeModal: boolean): Promise<void> {
      this.isLoadingData = true
      await this.projectService.sendAnswersAboutPendingInvitations(
        this.pendingProjects,
        this.loggedUser,
      )
      if (closeModal) {
        this.closeFirstConnectionModal()
        // refresh pending projects
        this.pendingProjects = await this.projectService.getPendingInvitationsToProject()
      }
      this.isLoadingData = false
    },

    async updateProjectsInEtablissement(): Promise<void> {
      this.isLoadingData = true
      await this.projectService.sendAnswersAboutPendingInvitations(
        this.projectsInEtablissement,
        this.loggedUser,
      )
      this.isLoadingData = false
      this.closeFirstConnectionModal()
      // refresh pending projects
      this.pendingProjects = await this.projectService.getPendingInvitationsToProject()
    },

    async createProject(): Promise<void> {
      let route = "/myProjects/new"
      if (
        this.loggedUser.profil === PROFIL_UTILISATEUR.ENSEIGNANT &&
        this.loggedUser.etablissement
      ) {
        this.chosenEtablissement = this.loggedUser.etablissement
        this.projectsInEtablissement = await this.projectService.getEtablissementProjects(
          this.chosenEtablissement,
        )
        if (this.projectsInEtablissement.length === 0) {
          route = `/myProjects/new/${this.loggedUser.etablissement.codeEtablissement}`
        } else {
          const projectsNotParticipatingIn = this.projectsInEtablissement.filter(
            (p) => !p.enseignants.some((e) => e.id === this.loggedUser.id),
          )

          if (projectsNotParticipatingIn.length === 0) {
            route = `/myProjects/new/${this.chosenEtablissement.codeEtablissement}/ask`
          } else {
            route = `/myProjects/new/${this.chosenEtablissement.codeEtablissement}/join`
          }
        }
      }
      this.$router.push(route)
    },

    async setChosenEtablissement(etablissement: EtablissementDTO): Promise<void> {
      this.chosenEtablissement = etablissement
      this.projectsInEtablissement = await this.projectService.getEtablissementProjects(
        this.chosenEtablissement,
      )
      if (this.projectsInEtablissement.length === 0) {
        this.$router.push(`/myProjects/new/${etablissement.codeEtablissement}`)
      } else {
        this.$router.push(`/myProjects/new/${etablissement.codeEtablissement}/join`)
      }
    },

    closeProjectCreation(): void {
      this.chosenEtablissement = new EtablissementDTO()
      this.$router.push("/myProjects")
    },

    async endProjectCreation(projectName: string): Promise<void> {
      const newProject = this.getProject("ACTIF", projectName, "")

      try {
        this.isLoadingData = true
        const projetAnnuel = await this.projectService.createProject(newProject)
        this.chosenEtablissement = new EtablissementDTO()
        this.closeProjectCreation()
        await this.loadProjects()
        if (projetAnnuel && projetAnnuel.projetId && projetAnnuel.id) {
          this.$router.push(
            RouterUtils.getRouteForProjectDetails(projetAnnuel.projetId, projetAnnuel.id),
          )
        }
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t("myProjects.create-project-error").toString(),
          e as Error,
        )
      }
    },

    async confirmJoinedProjects(): Promise<void> {
      await this.projectService.sendAnswersAboutPendingInvitations(
        this.projectsInEtablissement,
        this.loggedUser,
      )
      this.closeProjectCreation()
      this.loadProjects()
    },

    async askForProjectCreation(projectName: string, projectDescription: string): Promise<void> {
      const newProject = this.getProject("ACTIF", projectName, projectDescription)

      try {
        this.isLoadingData = true
        const projetAnnuel = await this.projectService.createProjectDemande(newProject)

        this.closeProjectCreation()
        await this.loadProjects()
        if (projetAnnuel && projetAnnuel.projetId && projetAnnuel.id) {
          this.$router.push(
            RouterUtils.getRouteForProjectDetails(projetAnnuel.projetId, projetAnnuel.id),
          )
        }
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t("myProjects.create-demande-error").toString(),
          e as Error,
        )
      }
      this.isLoadingData = false
    },

    getProject(etat: EtatProjet, projectName: string, projectDescription: string): ProjetAnnuelDTO {
      const newProject = new ProjetAnnuelDTO()
      newProject.nom = projectName
      newProject.etablissements = [this.chosenEtablissement]
      newProject.statut = "BROUILLON"
      newProject.etat = etat
      newProject.pitch = projectDescription || ""
      if (this.loggedUser.profil === PROFIL_UTILISATEUR.REFERENT) {
        if (this.loggedUser.structure) {
          newProject.structures = [this.loggedUser.structure]
        }
        newProject.referents = [this.loggedUser]
      } else if (this.loggedUser.profil === PROFIL_UTILISATEUR.ENSEIGNANT) {
        if (this.loggedUser.etablissement) {
          newProject.etablissements = [this.loggedUser.etablissement]
          newProject.enseignants = [this.loggedUser]
        }
      }
      return newProject
    },

    formatDate(date: Date): string {
      return DateUtils.formatDateFromPattern(date, "d MMMM yyyy")
    },
  },
})
</script>

<style lang="scss">
// Imported to have access to responsive breakpoints
@import "@/styles/variables";

$inside-padding: 0.5rem;

.MyProjectsPage-buttons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
}

.MyProjectsPage-cards {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 2rem;

  .ProjectCard {
    margin-left: 0.5rem;
  }

  padding-bottom: 20px;
}

.reduce-padding {
  padding: 0.3rem !important;
}

.date-sub-header {
  display: flex;
  justify-content: flex-end;
  gap: 20px;
  padding-right: 1vw;
  background: #fbfbfb;
}
.item-text {
  text-align: center;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;

  &.range {
    font-weight: 700;
  }
}

.MyProjectsPage-content {
  display: flex;
  min-height: calc(100vh - #{$footerHeight} - #{$header-height} - #{$HeaderInfo-height});
  height: 100%;
}

.MyProjectsPage-projects-part {
  flex-grow: 1;
  padding: $inside-padding $inside-padding 0;
}

.MyProjectsPage-actualites {
  min-width: $actualite-card-width;
  flex-shrink: 0;
  padding: $inside-padding $inside-padding 0;
  background-color: $instruction-background;
  border-top-right-radius: 10px;
  border-left: solid 1px $bookmarkcard-border-color;
}

@media screen and (max-width: $desktop) {
  .MyProjectsPage-content {
    flex-wrap: wrap;
  }
}
</style>
